import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, ClipboardList, Clock, AlertCircle, UserCog, Stethoscope } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NurseDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({
    pacientes_dia: 0,
    triajes_realizados: 0,
    citas_pendientes: 0,
    alertas: 0
  });
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      // Fetch assigned doctors
      const doctorsRes = await fetch(`${API_URL}/api/nurses/my-doctors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (doctorsRes.ok) {
        setAssignedDoctors(await doctorsRes.json());
      }

      // Fetch triajes
      const triajesRes = await fetch(`${API_URL}/api/triaje`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (triajesRes.ok) {
        const triajes = await triajesRes.json();
        const today = new Date().toISOString().split('T')[0];
        const todayTriajes = triajes.filter(t => t.created_at?.startsWith(today));
        setStats(prev => ({ ...prev, triajes_realizados: todayTriajes.length }));
      }

      // Fetch appointments
      const aptsRes = await fetch(`${API_URL}/api/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (aptsRes.ok) {
        const apts = await aptsRes.json();
        const pendingApts = apts.filter(a => a.status === 'pendiente');
        setStats(prev => ({ ...prev, citas_pendientes: pendingApts.length }));
      }

      // Fetch patients count
      const patientsRes = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (patientsRes.ok) {
        const patients = await patientsRes.json();
        setStats(prev => ({ ...prev, pacientes_dia: patients.length }));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Total Pacientes',
      value: stats.pacientes_dia,
      icon: Users,
      color: 'bg-teal-600',
      testId: 'patients-today-stat'
    },
    {
      name: 'Triajes Hoy',
      value: stats.triajes_realizados,
      icon: ClipboardList,
      color: 'bg-blue-600',
      testId: 'triajes-stat'
    },
    {
      name: 'Citas Pendientes',
      value: stats.citas_pendientes,
      icon: Clock,
      color: 'bg-orange-600',
      testId: 'pending-appointments-stat'
    },
    {
      name: 'Médicos Asignados',
      value: assignedDoctors.length,
      icon: Stethoscope,
      color: 'bg-purple-600',
      testId: 'assigned-doctors-stat'
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-title">
          Bienvenida, {user?.full_name}
        </h1>
        <p className="text-muted-foreground mt-2">
          Panel de Enfermería - Triaje y Coordinación
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Card key={stat.name} className="p-6 card-hover" data-testid={stat.testId}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/nurse/triaje">
              <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="quick-triaje-btn">
                <ClipboardList className="w-4 h-4 mr-2" />
                Nuevo Triaje
              </Button>
            </Link>
            <Link to="/nurse/patients">
              <Button variant="outline" className="w-full" data-testid="quick-patients-btn">
                <Users className="w-4 h-4 mr-2" />
                Ver Pacientes
              </Button>
            </Link>
            <Link to="/nurse/appointments">
              <Button variant="outline" className="w-full" data-testid="quick-appointments-btn">
                <Clock className="w-4 h-4 mr-2" />
                Ver Citas
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Assigned Doctors */}
      <Card data-testid="assigned-doctors-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-purple-600" />
            Médicos Asignados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assignedDoctors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserCog className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No tienes médicos asignados</p>
              <p className="text-sm">Un médico debe asignarte a su equipo</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedDoctors.map((doctor) => (
                <div 
                  key={doctor.id} 
                  className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-100"
                >
                  <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                    <span className="text-purple-700 font-bold">
                      {doctor.full_name?.charAt(0) || 'D'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Dr. {doctor.full_name}</p>
                    <p className="text-sm text-purple-600">{doctor.specialization}</p>
                    <p className="text-xs text-gray-500">{doctor.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
